/**
 * Tip-biztos API kliens — RFC 9457 hibákat ProblemDetails-re párolja,
 * trace_id-t propagálja, és Zod-dal validálja a választ.
 */
import { ProblemDetailsSchema, type ProblemDetails } from '@lectura/ts-contracts';
import type { z } from 'zod';
import { authFetch } from './auth';

// A `/api/<svc>/...` Next.js rewrite-on át megy a megfelelő microservice-re.
// A queries.ts-ben az endpoint pl. '/publications' → ténylegesen '/api/catalog/publications'.
const DEFAULT_API_BASE = '/api/catalog';

export class ApiError extends Error {
  constructor(
    public readonly problem: ProblemDetails,
    public readonly status: number,
  ) {
    super(problem.title);
    this.name = 'ApiError';
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /**
   * Manuális Bearer-token, ha a hívás NEM a Lectura-saját identity-svc-vel
   * dolgozó folyamat (pl. külső API). Alapesetben az `authFetch` kezeli.
   */
  accessToken?: string | null;
  /**
   * Egyedi base-URL — alapból `/api/catalog`, de pl. a progress-svc-hez
   * `/api/progress`-t kell adni. A `path` mindig erre fűződik.
   */
  baseUrl?: string;
}

function buildUrl(path: string, query?: RequestOptions['query'], baseUrl?: string): string {
  // A `new URL(relative)` egyetlen-arg hívással `TypeError: Invalid URL`-t dob.
  // Kell egy base. Kliens-oldalon `window.location.origin`-t használunk;
  // a fetch-nek visszaadjuk a relatív részt (`/api/...?query`).
  const isAbsolute = path.startsWith('http');
  const apiBase = baseUrl ?? DEFAULT_API_BASE;
  const fullPath = isAbsolute ? path : `${apiBase}${path}`;
  const base =
    typeof window !== 'undefined' && window.location
      ? window.location.origin
      : 'http://localhost';
  const url = new URL(fullPath, base);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue;
      url.searchParams.set(key, String(value));
    }
  }
  return isAbsolute ? url.toString() : `${url.pathname}${url.search}`;
}

/**
 * Rekurzív snake_case → camelCase kulcs-átalakítás. A backend (Pydantic / FastAPI)
 * snake_case JSON-t ad, a Zod-séma viszont camelCase-t vár.
 * Az értékeket nem érinti — `null`-okat és date-string-eket átengedi.
 */
function snakeToCamelKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(snakeToCamelKeys);
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const camelKey = k.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase());
      out[camelKey] = snakeToCamelKeys(v);
    }
    return out;
  }
  return value;
}

export async function apiRequest<T extends z.ZodTypeAny>(
  schema: T,
  path: string,
  options: RequestOptions = {},
): Promise<z.infer<T>> {
  const { body, query, accessToken, baseUrl, headers, ...rest } = options;
  const traceId = crypto.randomUUID();
  const init: RequestInit = {
    ...rest,
    headers: {
      Accept: 'application/json',
      'X-Trace-Id': traceId,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'include',
  };

  // Az `authFetch` minden Lectura-belüli (`/api/...`) requestre Bearer-tokent
  // tesz, ha a felhasználó be van jelentkezve, és 401-en automatikusan
  // megpróbál refresh-elni. Külső, abszolút URL-eknél (`http(s)://...`)
  // nincs auth — egyszerű `fetch`.
  const url = buildUrl(path, query, baseUrl);
  const response = url.startsWith('http') ? await fetch(url, init) : await authFetch(url, init);

  if (!response.ok) {
    let problem: ProblemDetails;
    try {
      problem = ProblemDetailsSchema.parse(await response.json());
    } catch {
      problem = {
        type: 'about:blank',
        title: response.statusText || 'Ismeretlen hiba',
        status: response.status,
        traceId,
      };
    }
    throw new ApiError(problem, response.status);
  }

  // 204 No Content
  if (response.status === 204) return undefined as z.infer<T>;

  const data = await response.json();
  const camel = snakeToCamelKeys(data);
  const parsed = schema.safeParse(camel);
  if (!parsed.success) {
    // Browser console-ra részletes hiba — könnyebb hibakeresés a /library oldalon
    // eslint-disable-next-line no-console
    console.error('[lectura] Zod parse fail', {
      path,
      issues: parsed.error.issues,
      received: camel,
    });
    throw new ApiError(
      {
        type: 'about:blank',
        title: 'Schema parse hiba',
        status: 200,
        detail: parsed.error.issues
          .map((i) => `${i.path.join('.')}: ${i.message}`)
          .join('; '),
        traceId,
      },
      200,
    );
  }
  return parsed.data;
}
